/**
 * Dashboard ad-detail APIs may return:
 * - `{ success, message, data: { appliedCount, …, data?: { ad fields } } }`
 * - camelCase recruiting metrics vs legacy names (`total_applicant`, …)
 * This merges shapes so Media Ad Details metric cards and panels populate.
 */
export function normalizeMediaAdDetailResponse(raw: unknown): any {
  if (raw == null || typeof raw !== "object") return raw

  const envelope = raw as Record<string, any>

  let root: Record<string, any>
  if (
    envelope.data &&
    typeof envelope.data === "object" &&
    !Array.isArray(envelope.data) &&
    (envelope.success !== undefined || envelope.message !== undefined)
  ) {
    root = { ...envelope.data, ...envelope }
  } else {
    root = { ...envelope }
  }

  const nested =
    root.data && typeof root.data === "object" && !Array.isArray(root.data)
      ? (root.data as Record<string, any>)
      : {}

  const pick = (...keys: string[]) => {
    for (const key of keys) {
      const v = root[key]
      if (v !== undefined && v !== null) return v
    }
    for (const key of keys) {
      const v = nested[key]
      if (v !== undefined && v !== null) return v
    }
    return undefined
  }

  const total_applicant = pick("total_applicant", "appliedCount")
  const total_contacted = pick("total_contacted", "screenContactedCount")
  const total_scheduled = pick("total_scheduled", "interviewAccepted")
  const total_attendees = pick("total_attendees", "screeningCount")
  const totalSignedRep = pick("totalSignedRep", "hireCount")
  const total_follow_up = pick("total_follow_up", "unableToContactCount")
  const interviewRejected = pick("interviewRejected")
  const costPerCandidate = pick(
    "costPerCandidate",
    "cost_per_candidate",
    "costPerApplicant"
  )
  const adAmount =
    pick("adAmount", "ad_amount") ?? nested.ad_amount ?? root.ad_amount

  const enriched: Record<string, any> = {
    ...root,
    total_applicant: total_applicant ?? root.total_applicant,
    total_contacted: total_contacted ?? root.total_contacted,
    total_scheduled: total_scheduled ?? root.total_scheduled,
    total_attendees: total_attendees ?? root.total_attendees,
    totalSignedRep: totalSignedRep ?? root.totalSignedRep,
    total_follow_up: total_follow_up ?? root.total_follow_up,
    interviewRejected:
      interviewRejected !== undefined ? interviewRejected : root.interviewRejected,
    costPerCandidate: costPerCandidate ?? root.costPerCandidate,
    adAmount: adAmount ?? root.adAmount,
  }

  if (!enriched.data && (root.city_name || root.state || nested.city_name)) {
    enriched.data = {
      ...nested,
      city_name: nested.city_name ?? root.city_name,
      state: nested.state ?? root.state,
      ad_type: nested.ad_type ?? root.ad_type,
      ad_status: nested.ad_status ?? root.ad_status,
      ad_approval_status: nested.ad_approval_status ?? root.ad_approval_status,
      zipCode: nested.zipCode ?? root.zipCode,
      createdAt: nested.createdAt ?? root.createdAt,
      meet_link: nested.meet_link ?? root.meet_link,
      salesTypeOption: nested.salesTypeOption ?? root.salesTypeOption,
      who_is_paying_for_ad:
        nested.who_is_paying_for_ad ?? root.who_is_paying_for_ad,
      bill_split_between: nested.bill_split_between ?? root.bill_split_between,
      personal_gmail_account:
        nested.personal_gmail_account ?? root.personal_gmail_account,
      package_start_date:
        nested.package_start_date ?? root.package_start_date,
      package_end_date: nested.package_end_date ?? root.package_end_date,
      ad_amount: nested.ad_amount ?? root.ad_amount,
      notes: nested.notes ?? root.notes,
      ad_spend: nested.ad_spend ?? root.ad_spend,
      recruiting_interview:
        nested.recruiting_interview ?? root.recruiting_interview,
    }
  }

  return enriched
}
